import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import "./index.scss"

import { cn, EnumsOf } from "@/lib/utils"

/**
 * @example
 * ```jsx
 *  <ProductTabs defaultValue="account">
 *       <ProductTabsList>
 *           <ProductTabsTrigger value="account" indicator="success">
 *                    Account
 *           </ProductTabsTrigger>
 *           <ProductTabsTrigger value="password" indicator="error">
 *                 Password
 *            </ProductTabsTrigger>
 *       </ProductTabsList>
 *
 *       <ProductTabsContent value="account">
 *           Make changes to your account here.
 *       </ProductTabsContent>
 *       <ProductTabsContent value="password">
 *           Change your password here.
 *       </ProductTabsContent>
 *  </ProductTabs>
 * ```
 */
function ProductTabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("product-tabs flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function ProductTabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        `
                    border-input-border flex w-full overflow-auto
                    border bg-white
                `,
        className
      )}
      {...props}
    />
  )
}

export const ProductTabIndicator = {
  Error: "Error",
  Success: "Success",
  Warning: "Warning",
  None: "None",
} as const
export type ProductTabIndicator = EnumsOf<typeof ProductTabIndicator>

type ProductTabsTriggerProps = React.ComponentProps<
  typeof TabsPrimitive.Trigger
> & {
  indicator?: ProductTabIndicator
}

function ProductTabsTrigger({
  className,
  indicator,
  ...props
}: ProductTabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        `
                    text-label-text data-[state=active]:text-button-black focus-visible:border-ring 
                    focus-visible:ring-ring/50 focus-visible:outline-ring border-input-border
                    active-indicator relative inline-flex flex-1 items-center 
                    justify-center gap-1.5 border-r px-2 py-2 text-sm
                    font-medium transition-[color,box-shadow] first:border-l-0
                    last:border-r-0 hover:cursor-pointer focus-visible:ring-[3px]
                    focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50
                    [&_svg]:pointer-events-none [&_svg]:shrink-0
                    [&_svg:not([class*='size-'])]:size-4 h-13 w-max text-nowrap
                `,
        indicator && `indicator ${indicator}`,
        className
      )}
      {...props}
    />
  )
}

function ProductTabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { ProductTabs, ProductTabsList, ProductTabsTrigger, ProductTabsContent }
